/**
 * The location detail's edit form — every operational field `LocationDetail` carries, in one form,
 * since the API's own `updateLocationSchema` accepts a partial patch of the same shape and there is
 * no reason to split what one PATCH already treats as one record.
 *
 * `LocationCreateForm.tsx` is the sibling create screen: the same field set, but built against
 * `createLocationSchema` directly rather than a patch, since a create has no existing detail to
 * default from. `estado` stays a free-text select-or-type field in both — the contract itself does
 * not constrain it, matching the badge's own fallback for an unrecognised value.
 */
import type { FieldError, LocationDetail, UpdateLocation } from '@apuestapr/contracts';
import { Button, Field, Input, Textarea, toast } from '@apuestapr/ui';
import { useId, useState, type FormEvent, type ReactElement } from 'react';

import type { ManagedLocation } from './hooks.ts';

interface FormState {
  readonly nombre: string;
  readonly ciudad: string;
  readonly direccion: string;
  readonly telefono: string;
  readonly estado: string;
  readonly notas: string;
  readonly ipAddress: string;
  readonly contactoNombre: string;
  readonly contactoTelefono: string;
  readonly licenciaJuego: string;
  readonly hasStaticIp: boolean;
  readonly isUpdated: boolean;
  readonly yubikeyAssigned: boolean;
  readonly ipWhitelisted: boolean;
  readonly training: boolean;
  readonly gcApproval: boolean;
  readonly otcUsersEnabled: boolean;
  readonly createLocationInKambi: boolean;
}

const formStateFrom = (detail: LocationDetail): FormState => ({
  nombre: detail.nombre,
  ciudad: detail.ciudad,
  direccion: detail.direccion,
  telefono: detail.telefono ?? '',
  estado: detail.estado,
  notas: detail.notas ?? '',
  ipAddress: detail.ipAddress ?? '',
  contactoNombre: detail.contactoNombre ?? '',
  contactoTelefono: detail.contactoTelefono ?? '',
  licenciaJuego: detail.licenciaJuego ?? '',
  hasStaticIp: detail.hasStaticIp,
  isUpdated: detail.isUpdated,
  yubikeyAssigned: detail.yubikeyAssigned,
  ipWhitelisted: detail.ipWhitelisted,
  training: detail.training,
  gcApproval: detail.gcApproval,
  otcUsersEnabled: detail.otcUsersEnabled,
  createLocationInKambi: detail.createLocationInKambi,
});

/** Blank text fields become `null`, matching how the mapper reads an absent value on the way in. */
const patchFrom = (state: FormState): UpdateLocation => ({
  nombre: state.nombre,
  ciudad: state.ciudad,
  direccion: state.direccion,
  telefono: state.telefono.trim() === '' ? null : state.telefono,
  estado: state.estado,
  notas: state.notas.trim() === '' ? null : state.notas,
  ipAddress: state.ipAddress.trim() === '' ? null : state.ipAddress,
  contactoNombre: state.contactoNombre.trim() === '' ? null : state.contactoNombre,
  contactoTelefono: state.contactoTelefono.trim() === '' ? null : state.contactoTelefono,
  licenciaJuego: state.licenciaJuego.trim() === '' ? null : state.licenciaJuego,
  hasStaticIp: state.hasStaticIp,
  isUpdated: state.isUpdated,
  yubikeyAssigned: state.yubikeyAssigned,
  ipWhitelisted: state.ipWhitelisted,
  training: state.training,
  gcApproval: state.gcApproval,
  otcUsersEnabled: state.otcUsersEnabled,
  createLocationInKambi: state.createLocationInKambi,
  tags: [],
});

const errorFor = (path: string, errors: readonly FieldError[]): string | undefined =>
  errors.find((error) => error.path === path)?.message;

interface CheckboxRowProps {
  readonly label: string;
  readonly checked: boolean;
  readonly disabled: boolean;
  readonly onChange: (checked: boolean) => void;
}

const CheckboxRow = ({ label, checked, disabled, onChange }: CheckboxRowProps): ReactElement => {
  const id = useId();
  return (
    <div className="flex items-center gap-2">
      <input
        id={id}
        type="checkbox"
        className="h-4 w-4 rounded border-input"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.currentTarget.checked)}
      />
      <label className="font-body text-sm text-foreground" htmlFor={id}>
        {label}
      </label>
    </div>
  );
};

export interface LocationEditFormProps {
  readonly detail: LocationDetail;
  readonly managed: ManagedLocation;
}

export const LocationEditForm = ({ detail, managed }: LocationEditFormProps): ReactElement => {
  const [state, setState] = useState<FormState>(() => formStateFrom(detail));
  const busy = managed.mutation.status === 'loading';
  const errors =
    managed.mutation.status === 'error' && managed.mutation.failure.kind === 'http'
      ? (managed.mutation.failure.details ?? [])
      : [];

  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    void managed.update(patchFrom(state)).then((result) => {
      if (result.ok) {
        toast({ title: 'Localidad actualizada' });
        setState(formStateFrom(result.data));
      }
    });
  };

  const set =
    <Key extends keyof FormState>(key: Key) =>
    (value: FormState[Key]): void =>
      setState((current) => ({ ...current, [key]: value }));

  return (
    <form className="grid gap-6" noValidate onSubmit={submit}>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Nombre" error={errorFor('nombre', errors)} required>
          {(control) => (
            <Input
              {...control}
              disabled={busy}
              value={state.nombre}
              onChange={(event) => set('nombre')(event.currentTarget.value)}
            />
          )}
        </Field>
        <Field label="Ciudad" error={errorFor('ciudad', errors)} required>
          {(control) => (
            <Input
              {...control}
              disabled={busy}
              value={state.ciudad}
              onChange={(event) => set('ciudad')(event.currentTarget.value)}
            />
          )}
        </Field>
        <Field
          className="sm:col-span-2"
          label="Dirección"
          error={errorFor('direccion', errors)}
          required
        >
          {(control) => (
            <Input
              {...control}
              disabled={busy}
              value={state.direccion}
              onChange={(event) => set('direccion')(event.currentTarget.value)}
            />
          )}
        </Field>
        <Field label="Teléfono" error={errorFor('telefono', errors)}>
          {(control) => (
            <Input
              {...control}
              disabled={busy}
              value={state.telefono}
              onChange={(event) => set('telefono')(event.currentTarget.value)}
            />
          )}
        </Field>
        <Field
          label="Estado"
          description="Texto libre: activo, no_activo, en_proceso, u otro valor operativo."
          error={errorFor('estado', errors)}
          required
        >
          {(control) => (
            <Input
              {...control}
              disabled={busy}
              value={state.estado}
              onChange={(event) => set('estado')(event.currentTarget.value)}
            />
          )}
        </Field>
      </div>

      <Field label="Notas" error={errorFor('notas', errors)}>
        {(control) => (
          <Textarea
            {...control}
            disabled={busy}
            rows={3}
            value={state.notas}
            onChange={(event) => set('notas')(event.currentTarget.value)}
          />
        )}
      </Field>

      <fieldset className="grid gap-4 rounded-xl border border-border p-4">
        <legend className="px-1 font-display text-sm font-medium">Contacto y red</legend>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Dirección IP" error={errorFor('ipAddress', errors)}>
            {(control) => (
              <Input
                {...control}
                disabled={busy}
                value={state.ipAddress}
                onChange={(event) => set('ipAddress')(event.currentTarget.value)}
              />
            )}
          </Field>
          <Field label="Licencia de juego" error={errorFor('licenciaJuego', errors)}>
            {(control) => (
              <Input
                {...control}
                disabled={busy}
                value={state.licenciaJuego}
                onChange={(event) => set('licenciaJuego')(event.currentTarget.value)}
              />
            )}
          </Field>
          <Field label="Contacto: nombre" error={errorFor('contactoNombre', errors)}>
            {(control) => (
              <Input
                {...control}
                disabled={busy}
                value={state.contactoNombre}
                onChange={(event) => set('contactoNombre')(event.currentTarget.value)}
              />
            )}
          </Field>
          <Field label="Contacto: teléfono" error={errorFor('contactoTelefono', errors)}>
            {(control) => (
              <Input
                {...control}
                disabled={busy}
                value={state.contactoTelefono}
                onChange={(event) => set('contactoTelefono')(event.currentTarget.value)}
              />
            )}
          </Field>
        </div>
      </fieldset>

      <fieldset className="grid gap-3 rounded-xl border border-border p-4 sm:grid-cols-2">
        <legend className="px-1 font-display text-sm font-medium">Estado operativo</legend>
        <CheckboxRow
          label="IP estática"
          checked={state.hasStaticIp}
          disabled={busy}
          onChange={set('hasStaticIp')}
        />
        <CheckboxRow
          label="Actualizada"
          checked={state.isUpdated}
          disabled={busy}
          onChange={set('isUpdated')}
        />
        <CheckboxRow
          label="YubiKey asignada"
          checked={state.yubikeyAssigned}
          disabled={busy}
          onChange={set('yubikeyAssigned')}
        />
        <CheckboxRow
          label="IP en lista blanca"
          checked={state.ipWhitelisted}
          disabled={busy}
          onChange={set('ipWhitelisted')}
        />
        <CheckboxRow
          label="Capacitación completada"
          checked={state.training}
          disabled={busy}
          onChange={set('training')}
        />
        <CheckboxRow
          label="Aprobación GC"
          checked={state.gcApproval}
          disabled={busy}
          onChange={set('gcApproval')}
        />
        <CheckboxRow
          label="Usuarios OTC habilitados"
          checked={state.otcUsersEnabled}
          disabled={busy}
          onChange={set('otcUsersEnabled')}
        />
        <CheckboxRow
          label="Crear localidad en Kambi"
          checked={state.createLocationInKambi}
          disabled={busy}
          onChange={set('createLocationInKambi')}
        />
      </fieldset>

      <div>
        <Button loading={busy} loadingLabel="Guardando localidad…" type="submit">
          Guardar cambios
        </Button>
      </div>
    </form>
  );
};
